import { useEffect, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, MapPin, AlertTriangle, ChevronRight } from "lucide-react";
import { getCityBySlug, CITIES } from "@/data/cities";

const ZoneCard = ({ zone }) => (
  <div data-testid="zone-card" className="flex gap-4 bg-white/[0.03] border border-white/10 rounded-2xl p-5">
    <AlertTriangle size={18} className="text-[#ffaa00] shrink-0 mt-0.5" />
    <div>
      <h3 className="font-heading font-semibold text-base text-white">{zone.title}</h3>
      <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{zone.text}</p>
    </div>
  </div>
);

const FaqCard = ({ faq }) => (
  <div data-testid="faq-card" className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
    <h3 className="font-heading font-semibold text-base text-white">{faq.q}</h3>
    <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{faq.a}</p>
  </div>
);

export default function CityPage({ slug }) {
  const city = getCityBySlug(slug);
  const otherCities = useMemo(() => CITIES.filter((c) => c.slug !== slug), [slug]);

  useEffect(() => {
    if (!city) return;
    document.title = `Can I Fly a Drone in ${city.name}? Complete Map & Rules Explained`;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: city.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    window.scrollTo(0, 0);
    return () => document.head.removeChild(script);
  }, [city]);

  if (!city) return <Navigate to="/" replace />;

  const mapLink = `/map?lat=${city.lat}&lng=${city.lng}&zoom=${city.zoom}`;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-100" data-testid="city-page">
      <div className="relative h-[52vh] min-h-[380px] overflow-hidden">
        <img src={city.hero} alt={`Drone view over ${city.name}`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/30" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 h-full flex flex-col justify-end pb-10">
          <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-[#2ec4b6] transition-colors mb-4 w-fit">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#2ec4b6] mb-3">
            {city.verdict}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
            Can I Fly a Drone in {city.name}?
          </h1>
          <p className="text-zinc-500 text-sm mt-2">Complete Map &amp; Rules Explained · Updated for CAP 722</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-12">
        <p className="text-base leading-relaxed text-zinc-300 max-w-2xl">{city.intro}</p>

        <Link
          to={mapLink}
          data-testid="open-map-cta"
          className="live-map-btn inline-flex items-center gap-3 bg-[#2ec4b6] text-black font-heading font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-[#3dd8ca] transition-colors"
        >
          <MapPin size={16} /> Check the live {city.name} drone map <ChevronRight size={16} />
        </Link>

        <section>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-6">
            {city.name} Drone Map Rules &amp; Flight Restriction Zones
          </h2>
          <div className="space-y-4">
            {city.zones.map((z) => (
              <ZoneCard key={z.title} zone={z} />
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {city.faqs.map((f) => (
              <FaqCard key={f.q} faq={f} />
            ))}
          </div>
        </section>

        <section className="border-t border-white/10 pt-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600 mb-4">More UK city drone guides</p>
          <div className="flex flex-wrap gap-2">
            {otherCities.map((c) => (
              <Link
                key={c.slug}
                to={`/can-i-fly-a-drone-in-${c.slug}`}
                data-testid="other-city-link"
                className="text-xs px-4 py-2 rounded-full border border-white/10 text-zinc-400 hover:border-[#2ec4b6]/50 hover:text-[#2ec4b6] transition-colors"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
